import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  firstValueFrom,
  from,
  Observable,
  switchMap,
} from 'rxjs';

import { ENDPOINTS } from '@shared/constants';
import {
  CommonResponseDTO,
  IEndpointRequest,
  IEndpointResponse,
  IMapping,
} from '@shared/interfaces';
import { generateURL } from '@shared/utils';
import { EncryptionService } from './encryption.service';
import Ajv, { ErrorObject } from 'ajv';

export type IEndpointResponseWithMappings = IEndpointResponse & {
  mappings: IMapping[];
};

@Injectable({
  providedIn: 'root',
})
export class EndpointsService {
  private readonly _dataStore = new BehaviorSubject<
    IEndpointResponseWithMappings[]
  >([]);
  public dataStore = this._dataStore.asObservable();

  constructor(
    private http: HttpClient,
    private readonly encryptionService: EncryptionService
  ) {}

  public async getEndpointsWithMappings(): Promise<
    IEndpointResponseWithMappings[]
  > {
    const loadedEndpoints = this._dataStore.value;

    if (loadedEndpoints.length > 0) return loadedEndpoints;

    const url = generateURL({
      endpoint: ENDPOINTS.INTEGRATION_ENDPOINTS_GET_ALL,
    });
    const config = { params: { with_mappings: 'true' } };
    const endpoints = await firstValueFrom(
      this.http.get<CommonResponseDTO<IEndpointResponseWithMappings[]>>(
        url,
        config
      )
    );

    this._dataStore.next(endpoints.data);

    return endpoints.data;
  }

  public getEndpoints(
    params: HttpParams
  ): Observable<CommonResponseDTO<IEndpointResponse[]>> {
    const url = generateURL({
      endpoint: ENDPOINTS.INTEGRATION_ENDPOINTS_GET_ALL,
    });
    const config = { params };
    return this.http.get<CommonResponseDTO<IEndpointResponse[]>>(url, config);
  }

  public createEndpoint(
    endpoint: IEndpointRequest
  ): Observable<CommonResponseDTO<IEndpointResponse>> {
    return from(this.encryptionService.encrypt(endpoint)).pipe(
      switchMap((encryptedEndpoint) => {
        const url = generateURL({
          endpoint: ENDPOINTS.INTEGRATION_ENDPOINTS_ADD_ONE,
        });

        return this.http.post<CommonResponseDTO<IEndpointResponse>>(
          url,
          encryptedEndpoint
        );
      })
    );
  }

  public updateAnEndpoint(
    endpoint: IEndpointRequest,
    id: string
  ): Observable<CommonResponseDTO<IEndpointResponse>> {
    return from(this.encryptionService.encrypt(endpoint)).pipe(
      switchMap((encryptedEndpoint) => {
        const url = generateURL({
          endpoint: ENDPOINTS.INTEGRATION_ENDPOINTS_UPDATE_ONE,
          params: { id },
        });

        return this.http.patch<CommonResponseDTO<IEndpointResponse>>(
          url,
          encryptedEndpoint
        );
      })
    );
  }

  public deleteEndpoint(
    id: string
  ): Observable<CommonResponseDTO<IEndpointResponse>> {
    const url = generateURL({
      endpoint: ENDPOINTS.INTEGRATION_ENDPOINTS_DELETE_ONE,
      params: { id },
    });
    return this.http.delete<CommonResponseDTO<IEndpointResponse>>(url);
  }

  public restoreEndpoint(
    id: string
  ): Observable<CommonResponseDTO<IEndpointResponse>> {
    const url = generateURL({
      endpoint: ENDPOINTS.INTEGRATION_ENDPOINTS_RESTORE_ONE,
      params: { id },
    });
    return this.http.patch<CommonResponseDTO<IEndpointResponse>>(url, {});
  }

  public async executeEndpoint(
    endpoint: IEndpointResponse,
    requestBody: any
  ): Promise<{
    success?: boolean;
    data?: any;
    error?: any;
    validation?: ErrorObject[];
  }> {
    const encryptedSQL = await this.encryptionService.encrypt(
      requestBody.requestBody
    );
    const reqBody = {
      pathParamData: requestBody.pathParamData,
      queryParamData: requestBody.queryParamData,
      bodyData: encryptedSQL,
    };
    const url = generateURL({
      endpoint: ENDPOINTS.INTEGRATION_ENDPOINTS_EXECUTE_ONE,
      params: { id: endpoint._id.toString() },
    });
    try {
      const response = await firstValueFrom(
        this.http.post<CommonResponseDTO<IEndpointResponse>>(url, reqBody)
      );

      const ajv = new Ajv();
      const schema = JSON.parse(endpoint?.response_schema);
      const validate = ajv.compile(schema);
      const isValid = validate(response.data);

      if (isValid) {
        return {
          success: response.success,
          data: response.data,
        };
      } else {
        return {
          success: false,
          data: response.data,
          validation: validate.errors,
        };
      }
    } catch (error) {
      return {
        success: false,
        error,
      };
    }
  }

  public verifyEndpoint(
    id: string
  ): Observable<CommonResponseDTO<IEndpointResponse>> {
    const url = generateURL({
      endpoint: ENDPOINTS.INTEGRATION_ENDPOINTS_VERIFY_ONE,
      params: { id },
    });
    return this.http.patch<CommonResponseDTO<IEndpointResponse>>(url, {});
  }
}
